// ============================================================================
//  NarrativeMedications.js — medications attached to a specific narrative.
// ----------------------------------------------------------------------------
//  Same pattern as NarrativeLabs: constrained to the imported NHIS medicines
//  list at the DB level. Dose is stored NHIS-style (amount + unit + numeric
//  days + optional auto-quantity) so rows can be reused in claims without any
//  free-text parsing.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  templateMedicines,
  templateNarrativeMeds, templateNarrativeMedRow, templateNarrativeMedsSuggest,
} from '../../BaseURL';
import notify from './notify';
import { IconPlus, IconSparkle, IconEdit, IconTrash, IconSearch, IconClose } from './icons';

// ── Controlled vocabularies — mirror NHIS claim shape and TotalCare UI ────
const FREQUENCIES = [
  { label: 'STAT',                dosesPerDay: 1 },
  { label: 'Once daily',          dosesPerDay: 1 },
  { label: 'Twice daily',         dosesPerDay: 2 },
  { label: 'Three times daily',   dosesPerDay: 3 },
  { label: 'Four times daily',    dosesPerDay: 4 },
  { label: 'Every 4 hours',       dosesPerDay: 6 },
  { label: 'Every 6 hours',       dosesPerDay: 4 },
  { label: 'Every 8 hours',       dosesPerDay: 3 },
  { label: 'Every 12 hours',      dosesPerDay: 2 },
  { label: 'PRN',                 dosesPerDay: null },
];
const ROUTES = ['Oral', 'IV', 'IM', 'SC', 'PR', 'INH', 'TOP', 'SL', 'Intranasal', 'Nebulised'];
const COMMON_UNITS = ['tab', 'cap', 'mL', 'mg', 'g', 'sachet', 'drop', 'puff', 'suppository'];

function freqPerDay(freq) {
  return FREQUENCIES.find((f) => f.label === freq)?.dosesPerDay || null;
}

// Auto-quantity heuristic: dose_amount × dosesPerDay × days. Only kicks in
// when all three are numeric; otherwise we leave it blank so the reviewer
// can set it manually (e.g. "1 course" for AL, "1 bottle" for suspension).
function computeAutoQuantity({ doseAmount, frequency, days }) {
  const amt = parseFloat(doseAmount);
  const per = freqPerDay(frequency);
  const d   = parseInt(days, 10);
  if (!Number.isFinite(amt) || !per || !Number.isFinite(d) || d <= 0) return '';
  const q = amt * per * d;
  return Number.isInteger(q) ? String(q) : q.toFixed(1);
}

export default function NarrativeMedications({ narrativeId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggesting, setSuggesting] = useState(false);
  const [picking, setPicking] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(templateNarrativeMeds(narrativeId));
      setRows(data.rows || []);
    } catch (err) {
      notify.error('Failed to load medications', err?.response?.data?.error || err.message);
    } finally { setLoading(false); }
  }, [narrativeId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (row) => {
    const ok = await notify.confirm(
      'Remove this medication?',
      `${row.medicine_code} · ${row.medicine_name} will be detached from this narrative.`,
      { confirmText: 'Remove', cancelText: 'Cancel', danger: true }
    );
    if (!ok) return;
    try {
      await axios.delete(templateNarrativeMedRow(narrativeId, row.id));
      notify.toast.success('Removed');
      load();
    } catch (err) {
      notify.error('Delete failed', err?.response?.data?.error || err.message);
    }
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await axios.post(templateNarrativeMedsSuggest(narrativeId), { count: 4 });
      const msg = data.rejectedOutOfCatalogue
        ? `${data.inserted} added · ${data.rejectedOutOfCatalogue} rejected (outside catalogue)`
        : `${data.inserted} medications added to this narrative`;
      notify.toast.success(msg);
      load();
    } catch (err) {
      notify.error('AI suggest failed', err?.response?.data?.error || err.message);
    } finally { setSuggesting(false); }
  };

  return (
    <div className="tp-narr-labs">
      <div className="tp-narr-labs-head">
        <div className="tp-narr-labs-title">
          <span className="tp-narr-section-label">Medications</span>
          <span className="tp-hint">
            Prescriptions from the NHIS medicines list — NHIS-shaped dose (amount, unit, frequency, days, quantity, route)
          </span>
        </div>
        <div className="tp-narr-labs-actions">
          <button type="button" className="tp-btn tp-btn-ghost tp-btn-sm"
                  onClick={suggest} disabled={suggesting}>
            <IconSparkle size={14} />
            {suggesting ? 'Asking AI…' : 'Suggest meds with AI'}
          </button>
          <button type="button" className="tp-btn tp-btn-primary tp-btn-sm" onClick={() => setPicking(true)}>
            <IconPlus size={14} /> Add medication
          </button>
        </div>
      </div>

      {loading && <div className="tp-hint">Loading medications…</div>}

      {!loading && rows.length === 0 && (
        <div className="tp-narr-labs-empty">
          No medications yet. Press <strong>Suggest meds with AI</strong> to have DeepSeek pick from the imported list,
          or <strong>Add medication</strong> to pick + prescribe one manually.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="tp-narr-labs-list">
          {rows.map((r) => (
            <MedCard key={r.id} row={r} onEdit={() => setEditing(r)} onDelete={() => remove(r)} />
          ))}
        </div>
      )}

      {picking && (
        <AddMedicationFlow
          narrativeId={narrativeId}
          onClose={() => setPicking(false)}
          onAdded={() => { setPicking(false); load(); }}
        />
      )}

      {editing && (
        <MedEditor
          narrativeId={narrativeId}
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

/* ── Single medication card — NHIS-shaped display ────────────────────── */
function MedCard({ row, onEdit, onDelete }) {
  const doseText = row.dose_amount
    ? [row.dose_amount, row.dose_unit].filter(Boolean).join(' ')
    : (row.dose || '');
  return (
    <div className="tp-lab-card">
      <div className="tp-lab-panel tp-lab-panel-request">
        <div className="tp-lab-panel-label">Prescription</div>
        <div className="tp-lab-request-body">
          <div className="tp-lab-code">{row.medicine_code}</div>
          <div className="tp-lab-name">
            {row.medicine_name}
            {row.strength && <span className="tp-med-strength"> · {row.strength}</span>}
            {row.dosage_form && <span className="tp-med-form"> · {row.dosage_form}</span>}
          </div>
        </div>
        <div className="tp-lab-actions" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="tp-icon-btn" onClick={onEdit} title="Edit prescription">
            <IconEdit size={14} />
          </button>
          <button type="button" className="tp-icon-btn danger" onClick={onDelete} title="Remove">
            <IconTrash size={14} />
          </button>
        </div>
      </div>

      <div className="tp-lab-panel tp-lab-panel-result">
        <div className="tp-lab-panel-label">How to take</div>
        {(doseText || row.frequency || row.days || row.route)
          ? (
            <table className="tp-med-grid">
              <tbody>
                <tr>
                  <td>Dose</td>       <td><strong>{doseText || '—'}</strong></td>
                  <td>Frequency</td>  <td><strong>{row.frequency || '—'}</strong></td>
                </tr>
                <tr>
                  <td>Days</td>       <td><strong>{row.days ?? '—'}</strong></td>
                  <td>Quantity</td>   <td><strong>{row.quantity || '—'}</strong></td>
                </tr>
                <tr>
                  <td>Route</td>      <td colSpan={3}><strong>{row.route || '—'}</strong></td>
                </tr>
              </tbody>
            </table>
          )
          : <div className="tp-hint">No dose set — press <strong>Edit</strong> to add one.</div>}
        {row.rationale && (
          <div className="tp-lab-summary">
            <span className="tp-lab-summary-label">Rationale</span>
            <span>{row.rationale}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── One-modal flow: pick a medication → fill dose → save ─────────────── */
function AddMedicationFlow({ narrativeId, onClose, onAdded }) {
  const [picked, setPicked] = useState(null);
  return (
    <div className="tp-modal-scrim" onClick={onClose}>
      <div className="tp-modal tp-modal-huge" onClick={(e) => e.stopPropagation()}>
        <div className="tp-modal-head-bar">
          <div>
            <h3>Add a medication</h3>
            <p className="tp-modal-sub">
              {picked
                ? 'Fill in the NHIS-shaped dose fields — quantity auto-calculates.'
                : 'Only medicines from the imported NHIS list can be prescribed.'}
            </p>
          </div>
          <button className="tp-icon-btn" onClick={onClose} title="Close">
            <IconClose size={16} />
          </button>
        </div>

        <div className="tp-editor-body">
          <div className="tp-editor-cols">
            <MedPickerPanel picked={picked} onPick={setPicked} />
            <DoseFormPanel
              narrativeId={narrativeId}
              picked={picked}
              onSaved={onAdded}
              onClear={() => setPicked(null)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── LEFT · Searchable medicines picker ──────────────────────────────── */
function MedPickerPanel({ picked, onPick }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(templateMedicines, { params: { q } });
        setRows(data.rows || []);
      } catch { notify.toast.error('Search failed'); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  return (
    <div className="tp-source-panel">
      <div className="tp-source-search">
        <IconSearch size={14} />
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search medication…" autoFocus />
      </div>

      <div className="tp-source-list tp-source-list-tall">
        {loading && <div className="tp-source-empty">Searching…</div>}
        {!loading && rows.length === 0 && (
          <div className="tp-source-empty">
            No matches. {q ? 'Try a broader term.' : 'Type to search.'}
          </div>
        )}
        {!loading && rows.map((r) => {
          const isPicked = picked?.id === r.id;
          return (
            <button
              key={r.id}
              type="button"
              className={`tp-source-row ${isPicked ? 'is-picked' : ''}`}
              onClick={() => onPick(r)}
            >
              <span className="tp-source-code">{r.code}</span>
              <span className="tp-source-name">
                {r.name}
                {r.strength ? ` · ${r.strength}` : ''}
                {r.dosage_form ? ` · ${r.dosage_form}` : ''}
              </span>
              <span className="tp-source-tags">
                {r.prescribing_level_code && <span className="tp-chip">Lvl {r.prescribing_level_code}</span>}
              </span>
              <span className="tp-source-choose">{isPicked ? 'Selected' : 'Pick'}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── RIGHT · NHIS-shaped dose form ───────────────────────────────────── */
function DoseFormPanel({ narrativeId, picked, onSaved, onClear }) {
  const [form, setForm] = useState({
    doseAmount: '',
    doseUnit:   '',
    frequency:  '',
    days:       '',
    quantity:   '',
    autoQty:    true,
    route:      '',
    rationale:  '',
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Reset the form whenever the picked drug changes so the user starts fresh.
  useEffect(() => {
    setForm((f) => ({
      ...f,
      // Suggest a sensible default unit from the drug's form
      doseUnit: picked?.dosage_form?.toLowerCase().includes('tablet') ? 'tab'
              : picked?.dosage_form?.toLowerCase().includes('capsule') ? 'cap'
              : picked?.dosage_form?.toLowerCase().includes('syrup') ? 'mL'
              : picked?.dosage_form?.toLowerCase().includes('suspension') ? 'mL'
              : picked?.dosage_form?.toLowerCase().includes('drop') ? 'drop'
              : picked?.dosage_form?.toLowerCase().includes('suppos') ? 'suppository'
              : picked?.dosage_form?.toLowerCase().includes('injection') ? 'mL'
              : f.doseUnit,
    }));
  }, [picked?.id, picked?.dosage_form]);

  // Auto-quantity: only computed when autoQty is on and inputs are numeric.
  useEffect(() => {
    if (!form.autoQty) return;
    const auto = computeAutoQuantity(form);
    if (auto !== form.quantity) setForm((f) => ({ ...f, quantity: auto }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.doseAmount, form.frequency, form.days, form.autoQty]);

  if (!picked) {
    return (
      <div className="tp-selection tp-selection-empty">
        <div className="tp-selection-empty-inner">
          <h4>No medication picked yet</h4>
          <p>Search the imported medicines list on the left and click one to prescribe.</p>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.doseAmount || !form.doseUnit || !form.frequency) {
      notify.toast.error('Dose amount, unit, and frequency are required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(templateNarrativeMeds(narrativeId), {
        medicineId: picked.id,
        doseAmount: form.doseAmount,
        doseUnit:   form.doseUnit,
        frequency:  form.frequency,
        days:       form.days || null,
        quantity:   form.quantity || null,
        route:      form.route,
        rationale:  form.rationale,
      });
      notify.toast.success(`${picked.name} added`);
      onSaved?.();
    } catch (err) {
      notify.error('Save failed', err?.response?.data?.error || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="tp-selection">
      <div className="tp-selection-card">
        <div className="tp-selection-head">
          <div>
            <div className="tp-selection-kind">Selected Medication</div>
            <div className="tp-selection-name">{picked.name}</div>
            <div className="tp-selection-meta">
              <span className="tp-chip">{picked.code}</span>
              {picked.strength && <span className="tp-chip">{picked.strength}</span>}
              {picked.dosage_form && <span className="tp-chip">{picked.dosage_form}</span>}
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={submit}>
        <div className="tp-field-row">
          <div className="tp-field">
            <label>Dose Amount</label>
            <input className="tp-input" value={form.doseAmount}
              onChange={(e) => update('doseAmount', e.target.value)}
              placeholder="e.g. 1 or 7.5" inputMode="decimal" />
          </div>
          <div className="tp-field">
            <label>Dose Unit</label>
            <input className="tp-input" list="tp-med-units" value={form.doseUnit}
              onChange={(e) => update('doseUnit', e.target.value)}
              placeholder="e.g. tab, mL, mg" />
            <datalist id="tp-med-units">
              {COMMON_UNITS.map((u) => <option key={u} value={u} />)}
            </datalist>
          </div>
        </div>

        <div className="tp-field-row">
          <div className="tp-field">
            <label>Frequency</label>
            <select className="tp-input" value={form.frequency}
              onChange={(e) => update('frequency', e.target.value)}>
              <option value="">— select —</option>
              {FREQUENCIES.map((f) => <option key={f.label} value={f.label}>{f.label}</option>)}
            </select>
          </div>
          <div className="tp-field">
            <label>Days</label>
            <input className="tp-input" value={form.days}
              onChange={(e) => update('days', e.target.value)}
              placeholder="e.g. 5" inputMode="numeric" />
          </div>
        </div>

        <div className="tp-field-row">
          <div className="tp-field">
            <label>
              Quantity
              <label className="tp-inline-check">
                <input type="checkbox" checked={form.autoQty}
                  onChange={(e) => update('autoQty', e.target.checked)} />
                Auto-calculate
              </label>
            </label>
            <input className="tp-input" value={form.quantity} disabled={form.autoQty}
              onChange={(e) => update('quantity', e.target.value)}
              placeholder={form.autoQty ? 'computed' : 'e.g. 30 or 1 course'} />
          </div>
          <div className="tp-field">
            <label>Route</label>
            <select className="tp-input" value={form.route}
              onChange={(e) => update('route', e.target.value)}>
              <option value="">— select —</option>
              {ROUTES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>

        <div className="tp-field">
          <label>Rationale (optional)</label>
          <textarea className="tp-input" rows={2} value={form.rationale}
            onChange={(e) => update('rationale', e.target.value)}
            placeholder="Short justification for this drug" />
        </div>

        <div className="tp-modal-actions">
          <button type="button" className="tp-btn tp-btn-ghost" onClick={onClear} disabled={saving}>
            Clear selection
          </button>
          <button type="submit" className="tp-btn tp-btn-primary" disabled={saving}>
            {saving ? 'Adding…' : 'Add medication'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── Editor for an existing row (same fields, prefilled) ─────────────── */
function MedEditor({ narrativeId, row, onClose, onSaved }) {
  const [form, setForm] = useState({
    doseAmount: row.dose_amount || '',
    doseUnit:   row.dose_unit || '',
    frequency:  row.frequency || '',
    days:       row.days ?? '',
    quantity:   row.quantity || '',
    autoQty:    !row.quantity, // if user set a manual value, leave it alone
    route:      row.route || '',
    rationale:  row.rationale || '',
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!form.autoQty) return;
    const auto = computeAutoQuantity(form);
    if (auto !== form.quantity) setForm((f) => ({ ...f, quantity: auto }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.doseAmount, form.frequency, form.days, form.autoQty]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(templateNarrativeMedRow(narrativeId, row.id), {
        doseAmount: form.doseAmount,
        doseUnit:   form.doseUnit,
        frequency:  form.frequency,
        days:       form.days || null,
        quantity:   form.quantity || null,
        route:      form.route,
        rationale:  form.rationale,
      });
      notify.toast.success('Prescription updated');
      onSaved?.();
    } catch (err) {
      notify.error('Save failed', err?.response?.data?.error || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="tp-modal-scrim" onClick={onClose}>
      <div className="tp-modal tp-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="tp-modal-head-bar">
          <div>
            <h3>Edit prescription</h3>
            <p className="tp-modal-sub">
              {row.medicine_code} · {row.medicine_name} {row.strength ? `· ${row.strength}` : ''}
            </p>
          </div>
          <button className="tp-icon-btn" onClick={onClose} title="Close">
            <IconClose size={16} />
          </button>
        </div>

        <form onSubmit={save} className="tp-editor-body" style={{ padding: '16px 24px' }}>
          <div className="tp-field-row">
            <div className="tp-field">
              <label>Dose Amount</label>
              <input className="tp-input" value={form.doseAmount}
                onChange={(e) => update('doseAmount', e.target.value)}
                placeholder="e.g. 1 or 7.5" inputMode="decimal" />
            </div>
            <div className="tp-field">
              <label>Dose Unit</label>
              <input className="tp-input" list="tp-med-units-edit" value={form.doseUnit}
                onChange={(e) => update('doseUnit', e.target.value)}
                placeholder="e.g. tab, mL, mg" />
              <datalist id="tp-med-units-edit">
                {COMMON_UNITS.map((u) => <option key={u} value={u} />)}
              </datalist>
            </div>
          </div>
          <div className="tp-field-row">
            <div className="tp-field">
              <label>Frequency</label>
              <select className="tp-input" value={form.frequency}
                onChange={(e) => update('frequency', e.target.value)}>
                <option value="">— select —</option>
                {FREQUENCIES.map((f) => <option key={f.label} value={f.label}>{f.label}</option>)}
              </select>
            </div>
            <div className="tp-field">
              <label>Days</label>
              <input className="tp-input" value={form.days}
                onChange={(e) => update('days', e.target.value)}
                inputMode="numeric" />
            </div>
          </div>
          <div className="tp-field-row">
            <div className="tp-field">
              <label>
                Quantity
                <label className="tp-inline-check">
                  <input type="checkbox" checked={form.autoQty}
                    onChange={(e) => update('autoQty', e.target.checked)} />
                  Auto-calculate
                </label>
              </label>
              <input className="tp-input" value={form.quantity} disabled={form.autoQty}
                onChange={(e) => update('quantity', e.target.value)} />
            </div>
            <div className="tp-field">
              <label>Route</label>
              <select className="tp-input" value={form.route}
                onChange={(e) => update('route', e.target.value)}>
                <option value="">— select —</option>
                {ROUTES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div className="tp-field">
            <label>Rationale (optional)</label>
            <textarea className="tp-input" rows={2} value={form.rationale}
              onChange={(e) => update('rationale', e.target.value)} />
          </div>

          <div className="tp-modal-actions">
            <button type="button" className="tp-btn tp-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="tp-btn tp-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save prescription'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
